import { LifecycleManagementDrawerOrdersDto } from "@lib/ShiOneClient";
import {
  ExpandMoreOutlined,
  KeyboardControlKeyOutlined,
} from "@mui/icons-material";
import { Box, Typography, Paper, useTheme } from "@mui/material";
import { formatCurrency } from "@utils/formatters/formatCurrency";
import { convertDateToLocalDateSlashes } from "functions/convertDate";
import { useState } from "react";

export const AssetLifecycleManagementDrawerOrdersItemDropdown = ({
  data,
  shouldHideOrderDetailsByDefault = false,
}: {
  data: LifecycleManagementDrawerOrdersDto;
  shouldHideOrderDetailsByDefault?: boolean;
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(
    !shouldHideOrderDetailsByDefault,
  );

  const quantity = data?.quantity ?? 0;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        py: 2,
        px: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box
          onClick={() => setIsExpanded((prev) => !prev)}
          sx={{ cursor: "pointer" }}
          role="button"
          aria-label="Toggle order details"
          aria-expanded={isExpanded}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              if (e.key === " ") e.preventDefault();
              setIsExpanded((prev) => !prev);
            }
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isExpanded ? (
              <KeyboardControlKeyOutlined />
            ) : (
              <ExpandMoreOutlined />
            )}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: theme.palette.text.primary }}
              >
                Order:{" "}
                {data?.order}
                {"  PO: "}
                {data?.poNumber}
              </Typography>

              <Typography variant="caption">
                Ordered on {data?.orderDate}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary }}
          >
            {`Total: ${formatCurrency(data?.previousPurchaseCost || 0)}`}
          </Typography>
          <Typography variant="caption">
            {`${quantity} covered asset${quantity === 1 ? "" : "s"}`}
          </Typography>
        </Box>
      </Box>
      {isExpanded && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mx: 2,
            mb: 2,
          }}
        >
          {data?.items?.map((item, index) => (
            <Paper
              elevation={0}
              key={data?.order + "-" + index}
              sx={{
                backgroundColor: theme.palette.action.hover,
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Box sx={{ flex: 4 }}>
                  <Typography variant="body2">{item?.description}</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Manufacturer Part:&nbsp;
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {item?.manufacturerPart}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        Start Date:&nbsp;
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {item?.startDate
                          ? convertDateToLocalDateSlashes(
                              item.startDate.toISOString(),
                            )
                          : ""}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        End Date:&nbsp;
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {item?.endDate}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ textAlign: { xs: "left", md: "right" }, flex: 1 }}>
                  <Typography variant="body2">
                    {formatCurrency(item?.previousPurchaseCost || 0)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Qty: {item?.licenseQty ?? "N/A"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {`${formatCurrency(item?.eachCost || 0)} each`}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};
